import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  BellIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  ChevronRightIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const NotificationCenter = ({ compact = false }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' | 'all'
  const [unreadNotifications, setUnreadNotifications] = useState({ grouped: [], others: [] });
  const [allNotifications, setAllNotifications] = useState({ grouped: [], others: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef(null);

  // 알림 아이콘 매핑
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
      case 'message_received':
      case 'new_chat_message':
        return <ChatBubbleLeftRightIcon className="h-5 w-5" />;
      case 'project_status':
      case 'project_assigned':
      case 'project_created':
        return <ClipboardDocumentCheckIcon className="h-5 w-5" />;
      case 'reward':
        return <CurrencyDollarIcon className="h-5 w-5" />;
      case 'notice':
        return <MegaphoneIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  // 알림 타입별 색상
  const getNotificationColor = (type) => {
    switch (type) {
      case 'message':
      case 'message_received':
      case 'new_chat_message':
        return 'text-blue-600 bg-blue-50';
      case 'project_status':
        return 'text-purple-600 bg-purple-50';
      case 'project_assigned':
        return 'text-green-600 bg-green-50';
      case 'project_created':
        return 'text-orange-600 bg-orange-50';
      case 'reward':
        return 'text-yellow-600 bg-yellow-50';
      case 'notice':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // 알림 목록 조회
  const fetchNotifications = async (unreadOnly = false, page = 1) => {
    if (!user) return;
    
    if (page === 1) setLoading(true);
    try {
      const response = await api.get('/notifications', {
        params: {
          unread_only: unreadOnly,
          page: page,
          limit: 10
        }
      });
      
      if (unreadOnly) {
        console.log('Unread notifications received:', response.data);
        setUnreadNotifications(response.data);
      } else {
        if (page === 1) {
          setAllNotifications(response.data);
        } else {
          // 페이지네이션 - 기존 데이터에 추가
          setAllNotifications(prev => ({
            ...prev,
            grouped: [...prev.grouped, ...response.data.grouped],
            others: [...prev.others, ...response.data.others]
          }));
        }
      }
      
      // 읽지 않은 알림 수 업데이트
      if (response.data.unreadCount !== undefined) {
        setUnreadCount(response.data.unreadCount);
      }
      
      setHasMore(response.data.hasMore || false);
      setCurrentPage(response.data.currentPage || page);
    } catch (error) {
      console.error('Fetch notifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 알림 클릭 처리
  const handleNotificationClick = async (notification) => {
    // 읽지 않은 알림이면 읽음 처리
    if (!notification.is_read) {
      try {
        await api.post('/notifications/mark-read', {
          notificationIds: [notification.id]
        });
        
        // 로컬 상태 업데이트 - 알림을 읽음 상태로 변경
        setUnreadNotifications(prev => {
          const newGrouped = prev.grouped.map(group => ({
            ...group,
            notifications: group.notifications.map(n => 
              n.id === notification.id ? { ...n, is_read: true } : n
            )
          }));
          
          const newOthers = prev.others.map(n => 
            n.id === notification.id ? { ...n, is_read: true } : n
          );
          
          return {
            ...prev,
            grouped: newGrouped,
            others: newOthers
          };
        });
        
        setAllNotifications(prev => {
          const newGrouped = prev.grouped.map(group => ({
            ...group,
            notifications: group.notifications.map(n => 
              n.id === notification.id ? { ...n, is_read: true } : n
            )
          }));
          
          const newOthers = prev.others.map(n => 
            n.id === notification.id ? { ...n, is_read: true } : n
          );
          
          return {
            ...prev,
            grouped: newGrouped,
            others: newOthers
          };
        });
        
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }

    // URL로 이동
    setIsOpen(false);
    
    if (notification.project_id) {
      const currentPath = location.pathname;
      const targetPath = `/projects/${notification.project_id}`;
      
      // 메시지 알림인 경우 채팅 탭으로 직접 이동
      if (notification.type === 'message' || notification.type === 'message_received' || notification.type === 'new_chat_message') {
        // 현재 같은 프로젝트 페이지에 있는 경우
        if (currentPath === targetPath) {
          navigate(`${targetPath}?tab=chat`, { replace: true });
        } else {
          navigate(`${targetPath}?tab=chat`);
        }
      } else {
        // 현재 같은 프로젝트 페이지에 있는 경우
        if (currentPath === targetPath) {
          navigate(targetPath, { replace: true });
        } else {
          navigate(targetPath);
        }
      }
    } else if (notification.url && notification.url.startsWith('/')) {
      navigate(notification.url);
    }
  };

  // 프로젝트 그룹 클릭 처리
  const handleProjectGroupClick = async (projectGroup) => {
    const unreadIds = projectGroup.notifications
      .filter(n => !n.is_read)
      .map(n => n.id);

    if (unreadIds.length > 0) {
      try {
        await api.post('/notifications/mark-read', {
          notificationIds: unreadIds
        });
        
        // 로컬 상태 업데이트 - 그룹 내 모든 알림을 읽음 상태로 변경
        setUnreadNotifications(prev => {
          const newGrouped = prev.grouped.map(group => {
            if (group.project.id === projectGroup.project.id) {
              return {
                ...group,
                notifications: group.notifications.map(n => 
                  unreadIds.includes(n.id) ? { ...n, is_read: true } : n
                )
              };
            }
            return group;
          });
          
          return {
            ...prev,
            grouped: newGrouped
          };
        });
        
        setAllNotifications(prev => {
          const newGrouped = prev.grouped.map(group => {
            if (group.project.id === projectGroup.project.id) {
              return {
                ...group,
                notifications: group.notifications.map(n => 
                  unreadIds.includes(n.id) ? { ...n, is_read: true } : n
                )
              };
            }
            return group;
          });
          
          return {
            ...prev,
            grouped: newGrouped
          };
        });
        
        setUnreadCount(prev => Math.max(0, prev - unreadIds.length));
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }

    setIsOpen(false);
    
    const currentPath = location.pathname;
    const targetPath = `/projects/${projectGroup.project.id}`;
    
    // 그룹에 메시지 알림이 있는지 확인
    const hasMessageNotification = projectGroup.notifications.some(n => 
      n.type === 'message' || n.type === 'message_received' || n.type === 'new_chat_message'
    );
    
    if (hasMessageNotification) {
      // 현재 같은 프로젝트 페이지에 있는 경우
      if (currentPath === targetPath) {
        navigate(`${targetPath}?tab=chat`, { replace: true });
      } else {
        navigate(`${targetPath}?tab=chat`);
      }
    } else {
      // 현재 같은 프로젝트 페이지에 있는 경우
      if (currentPath === targetPath) {
        navigate(targetPath, { replace: true });
      } else {
        navigate(targetPath);
      }
    }
  };

  // 전체 읽음 처리
  const markAllAsRead = async () => {
    const currentNotifications = activeTab === 'unread' ? unreadNotifications : allNotifications;
    const allUnreadIds = [
      ...currentNotifications.grouped.flatMap(g => 
        g.notifications.filter(n => !n.is_read).map(n => n.id)
      ),
      ...currentNotifications.others.filter(n => !n.is_read).map(n => n.id)
    ];

    if (allUnreadIds.length > 0) {
      try {
        await api.post('/notifications/mark-read', {
          notificationIds: allUnreadIds
        });
        
        setUnreadCount(0);
        
        // 다시 불러오기
        fetchNotifications(activeTab === 'unread', 1);
      } catch (error) {
        console.error('Mark all as read error:', error);
      }
    }
  };

  // 상대 시간 계산
  const getRelativeTime = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);
    
    if (diffInSeconds < 60) return '방금 전';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}분 전`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}시간 전`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}일 전`;
    
    return format(notificationDate, 'MM월 dd일', { locale: ko });
  };

  // 컴포넌트 마운트 시 알림 조회
  useEffect(() => {
    if (isOpen && user) {
      if (activeTab === 'unread') {
        fetchNotifications(true, 1);
      } else {
        fetchNotifications(false, 1);
      }
    }
  }, [isOpen, user, activeTab]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Socket.io 실시간 업데이트
  useEffect(() => {
    if (!user) return;

    const socket = window.io?.socket;
    if (!socket) return;

    const handleNewNotification = () => {
      // 새 알림을 받으면 두 탭 모두 업데이트
      fetchNotifications(true, 1);  // 새 알림 탭
      fetchNotifications(false, 1); // 전체 알림 탭
      // 알림 수 업데이트
      api.get('/notifications/unread-count')
        .then(response => {
          setUnreadCount(response.data.count);
        })
        .catch(console.error);
    };

    socket.on('new-notification', handleNewNotification);
    socket.on('notifications_read', handleNewNotification);
    socket.on('project_notifications_read', handleNewNotification);
    socket.on('message_notifications_read', handleNewNotification);

    return () => {
      socket.off('new-notification', handleNewNotification);
      socket.off('notifications_read', handleNewNotification);
      socket.off('project_notifications_read', handleNewNotification);
      socket.off('message_notifications_read', handleNewNotification);
    };
  }, [user]);

  // 초기 알림 수 조회
  useEffect(() => {
    if (user) {
      api.get('/notifications/unread-count')
        .then(response => {
          setUnreadCount(response.data.count);
        })
        .catch(console.error);
    }
  }, [user]);

  // 드롭다운 컨텐츠 렌더링
  const renderDropdownContent = () => {
    const currentNotifications = activeTab === 'unread' ? unreadNotifications : allNotifications;
    
    return (
    <>
      {/* 헤더 */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellIconSolid className="h-5 w-5 text-gray-700" />
            <h3 className="font-semibold text-gray-900">
              알림
              {unreadCount > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({unreadCount}개의 새 알림)
                </span>
              )}
            </h3>
          </div>
          {unreadCount > 0 && activeTab === 'unread' && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              모두 읽음
            </button>
          )}
        </div>
      </div>
      
      {/* 탭 */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('unread')}
          className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'unread'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          새 알림
          {unreadCount > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'all'
              ? 'text-blue-600 border-blue-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          전체 알림
        </button>
      </div>

      {/* 알림 목록 */}
      <div className="max-h-[60vh] sm:max-h-[480px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : currentNotifications.grouped.length === 0 && currentNotifications.others.length === 0 ? (
          <div className="text-center py-8">
            <ExclamationCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {activeTab === 'unread' ? '새로운 알림이 없습니다' : '알림이 없습니다'}
            </p>
          </div>
        ) : (
          <>
            {/* 프로젝트별 그룹화된 알림 */}
            {currentNotifications.grouped.map((group, idx) => {
              const hasUnread = group.notifications.some(n => !n.is_read);
              return (
                <div
                  key={idx}
                  className={`border-b border-gray-100 cursor-pointer transition-all ${
                    hasUnread 
                      ? 'bg-blue-50/50 hover:bg-blue-50/70' 
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => handleProjectGroupClick(group)}
                >
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {hasUnread && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse" />
                        )}
                        <ClipboardDocumentCheckIcon className={`h-5 w-5 flex-shrink-0 ${
                          hasUnread ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                        <h4 className={`font-medium text-sm truncate ${
                          hasUnread ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {group.project.title}
                        </h4>
                      </div>
                      <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                    </div>
                    
                    {/* 그룹 내 알림들 */}
                    <div className="space-y-1.5 ml-7">
                      {group.notifications.slice(0, 3).map((notification) => (
                        <div
                          key={notification.id}
                          className={`flex items-center gap-2 text-xs ${
                            notification.is_read 
                              ? 'text-gray-500' 
                              : 'text-gray-900 font-semibold'
                          }`}
                        >
                          <div className={`p-1 rounded-md ${
                            notification.is_read
                              ? 'bg-gray-100'
                              : getNotificationColor(notification.type)
                          } flex-shrink-0`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                          <span className="flex-1 min-w-0">
                            <span className={`block truncate ${
                              !notification.is_read && 'text-gray-900'
                            }`}>{notification.content}</span>
                          </span>
                          <span className={`flex-shrink-0 ml-1 ${
                            notification.is_read ? 'text-gray-400' : 'text-gray-600 font-medium'
                          }`}>
                            {getRelativeTime(notification.created_at)}
                          </span>
                        </div>
                      ))}
                      {group.notifications.length > 3 && (
                        <p className="text-xs text-gray-400 mt-1">
                          외 {group.notifications.length - 3}개의 알림
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* 기타 알림 */}
            {currentNotifications.others.map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-all relative ${
                  !notification.is_read 
                    ? 'bg-blue-50/50 hover:bg-blue-50/70' 
                    : 'bg-white hover:bg-gray-50'
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  {!notification.is_read && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  )}
                  <div className={`p-2 rounded-lg ${
                    notification.is_read
                      ? 'bg-gray-100'
                      : getNotificationColor(notification.type)
                  }`}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className={`text-sm break-words ${
                      notification.is_read 
                        ? 'text-gray-600' 
                        : 'text-gray-900 font-bold'
                    }`}>
                      {notification.title}
                    </p>
                    <p className={`text-xs mt-0.5 break-words line-clamp-2 ${
                      notification.is_read 
                        ? 'text-gray-500' 
                        : 'text-gray-700'
                    }`}>
                      {notification.content}
                    </p>
                    <p className={`text-xs mt-1 ${
                      notification.is_read 
                        ? 'text-gray-400' 
                        : 'text-gray-600 font-medium'
                    }`}>
                      {getRelativeTime(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* 푸터 */}
      <div className="px-4 py-3 border-t border-gray-200">
        {activeTab === 'all' && hasMore ? (
          <button
            onClick={() => fetchNotifications(false, currentPage + 1)}
            className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium py-2"
          >
            더 보기
          </button>
        ) : (
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/notifications');
            }}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            전체 알림 페이지로
            <ChevronRightIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    </>
  );
  };

  // 버튼 엘리먼트
  const buttonElement = (
    <button
ref={null}
      onClick={() => setIsOpen(!isOpen)}
      className={`p-1.5 rounded-lg transition-all relative ${
        isOpen 
          ? 'bg-blue-600 text-white hover:bg-blue-700' 
          : 'hover:bg-gray-100'
      }`}
    >
      <BellIcon className={`h-5 w-5 ${
        !compact ? 'translate-y-[3px]' : 'translate-y-0.5'
      } ${
        isOpen ? 'text-white' : 'text-gray-600'
      }`} />
      {unreadCount > 0 && (
        compact ? (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        ) : (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )
      )}
    </button>
  );

  // 드롭다운 엘리먼트
  const dropdownElement = isOpen && (
    <div className={`${
      compact 
        ? 'fixed sm:absolute top-16 sm:top-full sm:mt-2 left-4 sm:left-auto right-4 sm:right-0 w-auto sm:w-96' 
        : 'fixed sm:absolute top-16 sm:top-full sm:mt-2 left-4 sm:left-auto right-4 sm:right-0 w-auto sm:w-96'
    } bg-white rounded-lg shadow-xl border border-gray-200 z-[100]`}>
      {renderDropdownContent()}
    </div>
  );

  // 컴팩트 모드에서도 wrapper div 사용하여 이벤트 처리 정상화
  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        {buttonElement}
        {dropdownElement}
      </div>
    );
  }

  // 일반 모드에서는 wrapper div 사용
  return (
    <div className="relative" ref={dropdownRef}>
      {buttonElement}
      {dropdownElement}
    </div>
  );
};

export default NotificationCenter;